import { AlertTriangle } from 'lucide-react'

import { isApiError } from '@/shared/api/errors'
import { cn } from '@/shared/lib/cn'
import { DEFAULT_LOCALE, translate, useT } from '@/shared/i18n'

export interface ErrorStateProps {
  error?: unknown
  title?: string
  onRetry?: () => void
  className?: string
}

/**
 * El texto que se le muestra a la persona cuando algo falló.
 *
 * **Un mensaje de la API se muestra TAL CUAL.** El backend ya lo tradujo al
 * idioma de quien pregunta (`AppError.traducido`, ver `docs/i18n.md`):
 * volverlo a traducir acá sería adivinar sobre un texto que ya está bien, y
 * haría falta un catálogo paralelo con todos los errores del servidor.
 *
 * `fallback` es lo único nuestro, y por eso lo pasa el llamador ya traducido
 * con `t(...)`: esta función no es un componente y no tiene de dónde sacar el
 * idioma sin volverse un hook.
 */
export function errorMessage(error: unknown, fallback?: string): string {
  if (isApiError(error)) {
    // Un 422 trae `fieldErrors` con el detalle: qué campo y por qué. El
    // `message` de arriba es el sobre —"Los datos enviados no son válidos"—,
    // que no dice cuál es el problema ni cuál es el límite. Si el detalle está,
    // se muestra: es la diferencia entre un error que se puede corregir y uno
    // que sólo se puede reintentar a ciegas.
    const detalle = Object.values(error.fieldErrors).flat().filter(Boolean)
    if (detalle.length > 0) return detalle.join(' · ')
    return error.message
  }
  if (error instanceof Error && error.message) return error.message
  return fallback ?? translate(DEFAULT_LOCALE, 'common.state.errorDescription')
}

/** Tercer estado disenado de toda lista: error, siempre con "Reintentar". */
export function ErrorState({ error, title, onRetry, className }: ErrorStateProps) {
  const t = useT()
  const retryable = !isApiError(error) || error.isRetryable
  const requestId = isApiError(error) ? error.requestId : null

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-[var(--radius-card)] px-6 py-10 text-center',
        'border bg-surface-1',
        className,
      )}
      style={{ borderColor: 'hsl(var(--overdue) / .3)' }}
    >
      <AlertTriangle size={18} aria-hidden="true" style={{ color: 'hsl(var(--overdue))' }} />
      <p className="font-display text-base text-text-1">{title ?? t('common.state.errorTitle')}</p>
      <p className="max-w-[52ch] text-xs text-text-2">
        {errorMessage(error, t('common.state.errorDescription'))}
      </p>
      {requestId ? (
        <p className="num text-2xs text-text-3">{t('common.state.requestId', { requestId })}</p>
      ) : null}
      {onRetry && retryable ? (
        <button
          type="button"
          onClick={onRetry}
          className={cn(
            'hairline mt-2 h-7 rounded-[var(--radius-control)] px-3 text-xs text-text-1',
            'transition-colors duration-[var(--motion-fast)] hover:bg-surface-2',
          )}
        >
          {t('common.action.retry')}
        </button>
      ) : null}
    </div>
  )
}
